import { Component, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { environment } from "../../environments/environment";
import { PaymentRequest, StripeFormModel } from "./models/payments.models";
import { PaymentsService } from "./payments.service";
import { BaseComponent } from "../_shared/base.component";
import { productWatchService } from "../products/products.service";
import { MessageService } from "../layout/message/message.service";
import { take } from "rxjs/operators";

@Component({
  selector: "pm-payments",
  templateUrl: "./payments.component.html",
  styleUrls: ["./payments.component.css", "../app.component.css"],
})
export class PaymentsComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  constructor(
    private _activeRoute: ActivatedRoute,
    private paymentsService: PaymentsService,
    private _location: Location,
    router: Router,
    message: MessageService
  ) {
    super(router, message, false);
    this.states = this.paymentsService.getStateCodeArray();
  }
  public timeout = setTimeout;
  public api: string = environment.api;

  // Model inits
  public _customer = new StripeFormModel();
  public _request = new PaymentRequest();

  // used in current form
  card: any;
  cardNumber: any;
  cardExpiry: any;
  cardCvc: any;

  // transaction status flags
  isProcessing = false;
  errorMessage: string;
  paymentSuccess: boolean;
  productMessage = false;
  hideButton = false;

  // transaction information
  _transactionRequestGuid: string;
  _sname: string;
  transactionId: any;
  _stripe: any;

  // Form required values
  states: string[];
  paymentAlreadyProcessed: boolean;
  goBackUrlAddress = "https://www.sjcoe.org";

  ngOnInit() {
    this._activeRoute.paramMap.subscribe((params) => {
      if (
        params.has("description") &&
        params.has("amount") &&
        params.has("product")
      ) {
        this._request.Description = params.get("description");
        this._request.Amount = +params.get("amount");
        this._request.ProductId = +params.get("product");
        this._request.Date = new Date();
      this.checkQueryParams();
      } else if (params.has("transaction")) {
        this._transactionRequestGuid = params.get("transaction");
      }

      this._stripe = Stripe(environment.stripeKey);
    });
    this._activeRoute.queryParams.subscribe((params) => {
      if (params.sname) {
        this._sname = params.sname;
      }
    });
  }

  checkQueryParams() {
    this._activeRoute.queryParams.subscribe((params) => {
      this.formRelatedQueryString(params);
      this.devRelatedQueryString(params);
      // used internally from processing page
      this._request.ParticipantsUid = params.puid || undefined;
      // cannot be changed (used by tdev & cosmo)
      this._request.UserId = +params.user || 0;
      this._request.SName = params.sname || "";
      // new options
      this._request.EdjoinPaymentId = params.edjoinpid || undefined;
      this._request.EmailCcNotif = Boolean(params.cc) || false;
      // also add to customer model
      this.updateCustomer(this._request);
    });
  }
  private formRelatedQueryString(params) {
    // prepopulate helpers
    this._request.Name = params.name || undefined;
    this._request.Email = params.email || undefined;
    this._request.Phone = params.phone || undefined;
  }
  private devRelatedQueryString(params) {
    // dev-related strings
    this._request.SendReceipt = Boolean(params.receipt) || true;
    this._request.SkipConfirmation = Boolean(params.skipconf) || false;
  }

  ngAfterViewInit() {
      if (this._transactionRequestGuid == null) {
        this.paymentsService
          .createTransactionWithPaymentRequest(this._request)
          .subscribe(
            (rsp) => {
              const { TransactionId, SName, Product } = rsp;
              this._request.Product = Product;
              this._transactionRequestGuid = TransactionId;
              this._location.replaceState(
                `/payment/${this._transactionRequestGuid}?sname=${SName}`,
                "",
                "Payment Processing"
              );
              this.updateHeader(Product.PortalLogo, Product.Name);
              this.initCardView();
            },
            (e) => this.handleError(e)
          );
      } else {
        this.paymentsService
          .getRequestWithTransactionId(this._transactionRequestGuid)
          .subscribe(
            (rsp) => {
              const { Request } = rsp;
              this._request = Request;
              this.updateHeader(
                this._request.Product.PortalLogo,
                this._request.Product.Name
              );
              if (rsp.IsPaid === true) {
                this.handlePaymentResponse(rsp, false);
                this.paymentAlreadyProcessed = true;
                this.showInfoMessage("Info!", "Already paid for!");
                this.setErrorMessage(
                  "Transaction already processed. Press return to update your account."
                );
              } else {
                this._request.Date = new Date();
                this.updateCustomer(this._request);
                this.initCardView();
              }
            },
            (e) => this.handleError(e)
          );
      }
    // }
  }

  handleError(e: any) {
    this.updateHeader("default.png", "SJCOE");
    this.showErrorMessage("Error", e.error);
    this.setErrorMessage("Transaction Request Error.");
  }

  updateHeader(logo: string, title: string) {
    if (logo != null) {
      productWatchService.changeProductImage(logo);
      productWatchService.changeProductTitle(title);
    } else {
      productWatchService.changeProductImage("default.png");
      productWatchService.changeProductTitle("SJCOE");
    }
  }

  updateCustomer(data: PaymentRequest) {
    this._customer.Name = data.Name;
    this._customer.Email = data.Email;
    this._customer.Phone = data.Phone;
  }

  initCardView() {
    const { elements, elementStyles, elementClasses } = this.getStripeStyles();
    this.timeout(() => {
      this.cardNumber = elements.create("cardNumber", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardNumber.mount("#stripe-form-card-number");

      this.cardExpiry = elements.create("cardExpiry", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardExpiry.mount("#stripe-form-card-expiry");

      this.cardCvc = elements.create("cardCvc", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardCvc.mount("#stripe-form-card-cvc");

      this.registerElementsTs(
        [this.cardNumber, this.cardExpiry, this.cardCvc],
        "stripe-form",
        this.onFormSubmit
      );
    }, 200);
  }

  getAdditionalData() {
    return this._customer != null
      ? {
          name: this._customer.Name,
          address_line1: this._customer.Address || undefined,
          address_city: this._customer.City || undefined,
          address_state: this._customer.State || undefined,
          address_zip: this._customer.Zip,
          email: this._customer.Email,
        }
      : {};
  }

  async onFormSubmit(card: any) {
    // First, get a reference to the button
    var button = document.getElementById("btnSubmit");
    button.setAttribute("disabled", "");

    elements = this._stripe.elements();

    // if processing, ignore payment click
    if (this.isProcessing) {
      this.showInfoMessage(
        "Info",
        "Transaction still processing. Do not leave this page or browser"
      );
      return;
    }
    this.isProcessing = true;
    this.messageService.clearSticky();
    const validity = this._customer.isValid();
    if (validity.HasError) {
      this.showErrorMessage("Form Error", validity.Message);
      this.errorMessage = validity.Message;
      this.isProcessing = false;
      button.removeAttribute("disabled");
      return;
    }
    const additionalData = this.getAdditionalData();
    this.showInfoMessage("Transaction", "Initializing");
    // TODO pull some of this to service
    const { token, error } = await this._stripe.createToken(
      card,
      additionalData
    );
    if (error) {
      this.showErrorMessage("Stripe Error", error.message);
      this.isProcessing = false;
      button.removeAttribute("disabled");
    } else {
      const meta = [
        {
          Key: "OperatorId",
          Value: this._request.UserId.toString(),
        },
      ];
      if (this._customer.Phone) {
        meta.push({ Key: "Phone", Value: this._customer.Phone });
      }
      if (this._customer.Email) {
        meta.push({ Key: "Email", Value: this._customer.Email });
      }
      if (this._request.EdjoinPaymentId != null) {
        meta.push({
          Key: "EdjoinPaymentId",
          Value: this._request.EdjoinPaymentId,
        });
      }

      this.showInfoMessage(
        "Transaction",
        "Processing, Please stay on this page and do not close your browser. You’ll be redirected shortly"
      );

      this.paymentsService
        .postPayment({
          Name: this._customer.Name || "N/A",
          Token: token,
          Amount: this._request.Amount,
          Description: this._request.Description,
          Meta: meta,
          ProductId: this._request.ProductId,
          TransactionKey: this._transactionRequestGuid,
          ParticipantsUid: this._request.ParticipantsUid,
        })
        .subscribe(
          (response) => {
            if (environment.fullLog) {
              this.requestLog(
                JSON.stringify(response),
                JSON.stringify(this._request)
              );
            }
            this.handlePaymentResponse(response, true);
          },
          (e) => {
            this.showErrorMessage("Error", e.error);
            this.isProcessing = false;
            this.errorMessage = e.error;
            button.removeAttribute("disabled");
          }
        );
    }
  }

  handlePaymentResponse(response: any, isNew: boolean) {
    this.transactionId = isNew
      ? response.TransactionId
      : response.Request.TransactionId;
    const { sName, Message, Rts } = response;
    const skipConf = this._request.SkipConfirmation as boolean;
    const hasConf = this._request.Product.ConfirmationUrl != null;
    if (skipConf === false && hasConf) {
      // do not skip confirmation
      const { codeStack, edjoin } = environment.systems;
      let href = ``;
      let user = sName;
      if (this._sname) {
        user = this._sname;
      }
      switch (this._request.ProductId) {
        case codeStack.id:
          href = `${this._request.Product.ConfirmationUrl}&amt=${this._request.Amount}&tID=${this.transactionId}&user=${user}`;
          break;
        case edjoin.id:
          href = `${this._request.Product.ConfirmationUrl}?epid=${Rts}&msg=${Message}&tID=${response.TransactionId}`;
          break;
        default:
          href = `${this._request.Product.ConfirmationUrl}?Amt=${this._request.Amount}&uID=${this._request.UserId}&tID=${this.transactionId}`;
      }
      //console.log(href);
      //return;
      if (isNew) {
        document.location.href = href;
      } else {
        this.goBackUrlAddress = href;
      }
      this.requestLog(href, this.transactionId);
      return;
    } else {
      // confirmation skipped or confirmation url was not available
      this.showSuccessMessage("Transaction", "Success");
      this.setErrorMessage("Thank you.");
      this.hideButton = this._request.EdjoinPaymentId != null;
      this.paymentSuccess = true;
      this.isProcessing = false;
      this.requestLog(
        `NoConfirmUrl: ${this._request.Product.Id}:${this._request.Product.Name}, Amt: ${this._request.Amount}`,
        response.TransactionId
      );
    }
  }

  requestLog(href: string, transactionId: string) {
    this.paymentsService.requestLog(href, transactionId).subscribe();
  }
  goBackUrl(): void {
    document.location.replace(this.goBackUrlAddress);
  }

  // Using Stripe.js & Elements
  ngOnDestroy(): void {}

  setErrorMessage(msg: string) {
    this.productMessage = true;
    this.errorMessage = msg;
  }

  registerElementsTs(
    elements: HTMLElement[],
    name: string,
    callback: (this: PaymentsComponent, element: any) => any
  ) {
    const formClass = "." + name;
    const example = document.querySelector(formClass) as HTMLElement;
    const form = example.querySelector("form");

    elements.forEach((x) => {
      if (x.querySelector instanceof Function) {
        x = x.tagName === "INPUT" ? x : x.querySelector("input");
        x.addEventListener("change", (res: ErrorEvent) => {
          if (res.error) {
            if (this.errorMessage != null) {
              this.errorMessage += `, ${res.error.message}`;
            } else {
              this.errorMessage = res.error.message;
            }
          } else {
            this.errorMessage = null;
          }
        });
      }
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      callback.call(this, elements[0]);
    });
  }

  getStripeStyles(): any {
    const elements = this._stripe.elements({
      fonts: [
        {
          cssSrc: "https://fonts.googleapis.com/css?family=Quicksand",
        },
      ],
      locale: "auto",
    });
    const elementStyles = {
      base: {
        color: "#495057",
        fontFamily: "Open Sans, Segoe UI, sans-serif",
        fontSize: "16px",
        fontSmoothing: "antialias-ed",
        letterSpacing: "0.8px",
        lineHeight: "1.5",
        ":focus": {
          color: "#495057",
        },
        "::placeholder": {
          color: "#ccc",
        },
        ":focus::placeholder": {
          color: "#ccc",
        },
      },
      invalid: {
        color: "#000",
        ":focus": {
          color: "#FA755A",
        },
        "::placeholder": {
          color: "#FFCCA5",
        },
      },
    };
    const elementClasses = {
      focus: "focus",
      empty: "empty",
      invalid: "invalid",
    };

    return { elements, elementStyles, elementClasses };
  }
}
